import { Match } from './../../models/match';
import { HttpClient } from '@angular/common/http';
import { BaseEntityHttpService } from '../../shared/base.httpservice';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ListRequestOptions, ResourceLoadResponse } from '../../shared/state';
import { mergeMap } from 'rxjs/operators';
import { MatchUtilitiesService } from './match-utilities.service';
import { GamificationResult } from '../../models/gamification';
import { StraightActionType } from '../../models/straightactiontype';
import { StraightMatchAction } from '../../models/straightmatchaction';
import { MatchScoreAction } from '../../models/matchscoreaction';
import { MatchPause } from '../../models/matchpause';

@Injectable()
export class MatchesHttpService extends BaseEntityHttpService<Match> {
	protected endpoint = this.apiUrl('matches');

	constructor(http: HttpClient, private matchUtilitiesService: MatchUtilitiesService) {
		super(http);
	}

	load(query?: ListRequestOptions): Observable<ResourceLoadResponse<Match>> {
		return this.http.get<ResourceLoadResponse<Match>>(`${this.endpoint}/List`, { params: query as any }).pipe(
			mergeMap(matches => this.matchUtilitiesService.mapArrayAsObservable(matches))
		);
	}

	getById(id: string): Observable<Match> {
		return this.http.get<Match>(`${this.endpoint}/${id}`).pipe(mergeMap(match => this.matchUtilitiesService.mapAsObservable(match)));
	}

	create(match: Match): Observable<Match> {
		return this.http.post<Match>(`${this.endpoint}/create`, match);
	}

	updateScore(match: Match): Observable<any> {
		return this.http.post(`${this.endpoint}/updatescore`, {
			id: match.id,
			scoreChallenger: match.scoreChallenger,
			scoreChallenged: match.scoreChallenged
		});
	}

	// Scores one set of a multiball match - separate from updateScore above, which is still what carries
	// the match's own "sets won" tally (see DOMAIN.md).
	updateSetScore(matchId: string, matchSetId: string, scoreChallenger: number, scoreChallenged: number): Observable<any> {
		return this.http.post(`${this.endpoint}/updatesetscore`, {
			id: matchId,
			matchSetId,
			scoreChallenger,
			scoreChallenged
		});
	}

	completeMatch(match: Match): Observable<{ gamification: GamificationResult }> {
		return this.http.post<{ gamification: GamificationResult }>(`${this.endpoint}/complete`, {
			"Id": match.id
		});
	}

	// The challenged player accepting a match - see MatchManager.CreateAsync/ConfirmAsync. Only legal
	// while the match is still MatchStatus.Challenged.
	acceptMatch(match: Match): Observable<any> {
		return this.http.post(`${this.endpoint}/accept`, {
			"Id": match.id
		});
	}

	// The challenged player declining a match - mirrors acceptMatch above, same Challenged-only guard.
	declineMatch(match: Match): Observable<any> {
		return this.http.post(`${this.endpoint}/decline`, {
			"Id": match.id
		});
	}

	// Opponent confirmation of a submitted tournament match score - only legal while the match is
	// Completed (a tournament with Tournament.requireMatchVerification set). See DOMAIN.md.
	verifyMatch(match: Match): Observable<{ gamification: GamificationResult }> {
		return this.http.post<{ gamification: GamificationResult }>(`${this.endpoint}/verify`, {
			"Id": match.id
		});
	}

	// Disputes the submitted score rather than the match itself - reopens score entry. Mirrors
	// verifyMatch above, same Completed-only guard.
	disputeMatch(match: Match): Observable<any> {
		return this.http.post(`${this.endpoint}/dispute`, {
			"Id": match.id
		});
	}

	getMatches(top: number, skip: number, sortType: string, searchStr: string): Observable<Match[]> {
		// 	var url = "matches/list?$top=" + top;
		// 	url += "&$skip=" + skip;
		// 	url += "&$orderby=" + sortType;
		// 	if(searchStr != ""){
		// 		url += '&' + this.getODATASearchQuery(searchStr, ['Opponent/Name']);
		// 	}
		// 	return this.http.get<Match[]>(url).pipe(map((matches:Match[]) => this.mapArray(matches)));
		return null;
	}

	updateStraightMatch(matchId: string, action: StraightActionType): Observable<any> {
		return this.http.post(`${this.endpoint}/updatestraightscore`, {
			matchId,
			action
		});
	}

	getStraightActions(matchId: string): Observable<StraightMatchAction[]> {
		return this.http.get<StraightMatchAction[]>(`${this.endpoint}/${matchId}/straightactions`);
	}

	getScoreActions(matchId: string): Observable<MatchScoreAction[]> {
		return this.http.get<MatchScoreAction[]>(`${this.endpoint}/${matchId}/scoreactions`);
	}

	getPauses(matchId: string): Observable<MatchPause[]> {
		return this.http.get<MatchPause[]>(`${this.endpoint}/${matchId}/pauses`);
	}

	// Tournament-match pause - only legal once per player per match, see MatchManager.PauseAsync.
	pauseMatch(match: Match): Observable<any> {
		return this.http.post(`${this.endpoint}/pause`, {
			"Id": match.id
		});
	}

	// Idempotent - safe to call even if the pause already ended, see MatchManager.ResumeAsync.
	resumeMatch(match: Match): Observable<any> {
		return this.http.post(`${this.endpoint}/resume`, {
			"Id": match.id
		});
	}
}
